import cn from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import isAddElementsAllowed from 'editor/src/store/editor/selector/isAddElementsAllowed';
import setSidebarActiveTabOperation from 'editor/src/store/editorModules/sidebar/operation/setSidebarActiveTabOperation';
import insertTextLibraryEntryOperation from 'editor/src/store/editorModules/textLibrary/operation/insertTextLibraryEntryOperation';
import getTextLibraryAppendTarget from 'editor/src/store/editorModules/textLibrary/selector/getTextLibraryAppendTarget';
import { TextLibraryEntry } from 'editor/src/store/editorModules/textLibrary/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import Button from 'editor/src/component/Button';
import Checkbox from 'editor/src/component/Checkbox';
import IconArrowLeft from 'editor/src/component/Icon/IconArrowLeft';
import { useIsMobile } from 'editor/src/component/useDetectDeviceType';

import styles from './index.module.scss';

interface Props {
  entry: TextLibraryEntry;
  onBack: () => void;
}

function EntryDetails({ entry, onBack }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const addElementsAllowed = useSelector(isAddElementsAllowed);
  const appendTarget = useSelector(getTextLibraryAppendTarget);
  const includeTitleByDefault = useSelector((state) => state.editorModules.textLibrary.includeTitle);
  const [includeTitle, setIncludeTitle] = useState(includeTitleByDefault);
  const isMobile = useIsMobile();
  const singlePart = entry.verses.length === 1;
  // every verse is chosen by default
  const [selected, setSelected] = useState<boolean[]>(() => entry.verses.map(() => true));
  const selectedIndexes = selected.flatMap((on, index) => (on ? [index] : []));
  const allSelected = selectedIndexes.length === entry.verses.length;

  const toggleVerse = (index: number, on: boolean) => {
    setSelected((prev) => prev.map((value, i) => (i === index ? on : value)));
  };

  const onInsert = () => {
    const inserted = dispatch(insertTextLibraryEntryOperation(entry, selectedIndexes, includeTitle));
    if (inserted && isMobile) {
      // close the sheet so the customer sees the inserted text
      dispatch(setSidebarActiveTabOperation(-1));
    }
  };

  return (
    <div className={styles.details}>
      <button type="button" className={cn(styles.back, 'cy-text-library-back')} onClick={onBack}>
        <IconArrowLeft />
        {t('editor-text-library-back-to-results')}
      </button>
      <div className={styles.detailsTitle}>
        {entry.number && <span className={styles.number}>{entry.number}</span>}
        <span>{entry.title}</span>
      </div>
      {entry.credits && <div className={styles.credits}>{entry.credits}</div>}
      <Checkbox
        className={cn(styles.includeTitle, 'cy-text-library-include-title')}
        on={includeTitle}
        onChange={setIncludeTitle}
      >
        {t('editor-text-library-include-title')}
      </Checkbox>
      {singlePart ? (
        <div className={cn(styles.singleText, 'cy-text-library-single-text')}>{entry.verses[0]}</div>
      ) : (
        <>
          <div className={styles.versesHeader}>
            <span>{t('editor-text-library-verses')}</span>
            <button
              type="button"
              className={cn(styles.link, 'cy-text-library-toggle-all')}
              onClick={() => setSelected(entry.verses.map(() => !allSelected))}
            >
              {allSelected ? t('editor-text-library-clear-all') : t('Select all')}
            </button>
          </div>
          <div className={styles.verses}>
            {entry.verses.map((verse, index) => (
              <Checkbox
                // verses are a fixed list for a given entry
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                className={cn(styles.verse, 'cy-text-library-verse')}
                on={selected[index]}
                onChange={(on) => toggleVerse(index, on)}
              >
                <span className={styles.verseNumber}>{index + 1}</span>
                <span className={styles.verseText}>{verse}</span>
              </Checkbox>
            ))}
          </div>
        </>
      )}
      <Button
        primary
        fullWidth
        className={cn(styles.insert, 'cy-text-library-insert')}
        disabled={(!addElementsAllowed && !appendTarget) || !selectedIndexes.length}
        onClick={onInsert}
      >
        {appendTarget ? t('editor-text-library-add-to-selected') : t('editor-insert')}
      </Button>
      {!appendTarget && <div className={styles.hint}>{t('editor-text-library-select-text-tip')}</div>}
    </div>
  );
}

export default React.memo(EntryDetails);
